import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Toast from '@root/components/toast';
import { IPackageDetail } from '@root/types/package';
import { useSubscriptionDetailData } from './useDetail';
import { subscriptionApi } from '@root/api';
import Modal from '@root/components/modal';
import { useUnBindDevicesAction } from '../useUnBindDevices';
import { useSubscriptionsState } from '@root/state';

const useRemoveDevice = (): {
  detail: IPackageDetail;
  loading: number;
  getDetail: () => void;
  onRemove: (deviceId: string, lockStatus: number) => void;
} => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { getUnBindDevices } = useUnBindDevicesAction();
  const { detail, loading, getDetail } = useSubscriptionDetailData(id);
  const expiredSubscriptionList = useSubscriptionsState(
    (state) => state.setExpiredData,
  );
  const { changeStatus } = detail;

  const _onRemove = useCallback(
    (deviceId: string) => {
      if (!id) {
        return;
      }

      Toast.loading({}, 0, 0);

      subscriptionApi
        .deviceSwitchPackage({
          deviceIds: [deviceId],
          packageId: id,
          type: 1,
        })
        .then(() => {
          Toast.destroy();
          Toast.info(t('remove_success'));
          // 更新详情
          getDetail();
          // 首页数据为过期数据
          expiredSubscriptionList();
          // 更新未绑定设备
          getUnBindDevices();
        })
        .catch(() => {
          Toast.destroy();
        });
    },
    [expiredSubscriptionList, getDetail, getUnBindDevices, id, t],
  );

  const onRemove = useCallback(
    (deviceId: string, lockStatus: number) => {
      // 在升降级过程中，并且设备被锁定的状态， 不允许删除
      if (changeStatus && lockStatus) {
        Modal.alert({
          title: '',
          content: t('remove_device_lock_tips'),
          confirm: t('ok'),
        });
        return;
      }

      Modal.confirm({
        id: 'remove-device',
        title: t('remove_device_confirm_title'),
        content: t('remove_device_confirm_context'),
        cancel: t('cancel'),
        confirm: t('ok'),
        onConfirm: () => {
          _onRemove(deviceId);
        },
      });
    },
    [_onRemove, changeStatus, t],
  );

  useEffect(() => {
    return () => {
      Toast.destroy();
      Modal.close();
    };
  }, []);

  return {
    detail,
    loading,
    getDetail,
    onRemove,
  };
};

export default useRemoveDevice;
